import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Snackbar
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { Refresh, Visibility, Error as ErrorIcon, Add } from '@material-ui/icons';
import ReactJson from 'react-json-view';
import { useMezzanine } from '../../context/MezzanineContext';
import ImportProfileForm from './ImportProfileForm';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  tableCell: {
    padding: '10px 16px',
    fontSize: '12px',
  },
  headerCell: {
    padding: '8px 16px',
    backgroundColor: 'white',
  },
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      padding: '2px 8px',
      fontSize: '12px',
      backgroundColor: 'white',
      '& fieldset': {
        borderColor: '#ccc',
      },
      '&:hover fieldset': {
        borderColor: '#999',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#ff6f00',
      },
    },
    '& .MuiAutocomplete-input': {
      padding: '4px 4px',
      fontSize: '12px',
    },
  },
  typeBox: {
    display: 'inline-block',
    padding: '2px 10px',
    border: '1px solid #999',
    borderRadius: '4px',
    fontSize: '11px',
    textTransform: 'uppercase',
    backgroundColor: 'white',
  },
}));

const Profiles = () => {
  const classes = useStyles();
  const { profiles, profilesLoading, profilesError, fetchProfiles } = useMezzanine();
  const [filters, setFilters] = useState({ label: 'all', type: 'all' });
  const [viewModal, setViewModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [importFormOpen, setImportFormOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleView = (profile) => {
    setSelectedProfile(profile);
    setViewModal(true);
  };

  // Get unique values for filter dropdowns
  const getUniqueValues = (field) => {
    if (field === 'label') {
      return [...new Set(profiles.map(item => item.label).filter(Boolean))];
    }
    if (field === 'type') {
      return [...new Set(profiles.map(item => item.profile_type).filter(Boolean))];
    }
    return [];
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value || 'all' });
  };

  // Filter data based on filters
  const filteredData = useMemo(() => {
    return profiles.filter(profile => {
      const labelMatch = filters.label === 'all' || profile.label === filters.label;
      const typeMatch = filters.type === 'all' || profile.profile_type === filters.type;
      return labelMatch && typeMatch;
    });
  }, [profiles, filters]);

  // Loading state
  if (profilesLoading && profiles.length === 0) {
    return (
      <Box className={classes.container} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress style={{ color: '#ff6f00' }} />
      </Box>
    );
  }

  // Error state
  if (profilesError && profiles.length === 0) {
    return (
      <Box className={classes.container}>
        <Paper style={{ padding: '20px', backgroundColor: '#ffebee', textAlign: 'center' }}>
          <ErrorIcon style={{ color: '#c62828', fontSize: '48px' }} />
          <Typography variant="h6" style={{ color: '#c62828', marginTop: '10px' }}>
            Failed to load profiles
          </Typography>
          <Typography variant="body2" style={{ color: '#c62828', marginBottom: '15px' }}>
            {profilesError}
          </Typography>
          <IconButton onClick={fetchProfiles} style={{ color: '#c62828', border: '2px solid #c62828' }}>
            <Refresh />
          </IconButton>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Import Button and Refresh */}
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: '16px' }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setImportFormOpen(true)}
          style={{ backgroundColor: '#ff6f00', color: 'white' }}
        >
          Import
        </Button>
        <IconButton
          onClick={fetchProfiles}
          disabled={profilesLoading}
          size="small"
          style={{ color: '#ff6f00', border: '1px solid #ff6f00', borderRadius: '4px', padding: '6px' }}
        >
          {profilesLoading ? <CircularProgress size={20} style={{ color: '#ff6f00' }} /> : <Refresh fontSize="small" />}
        </IconButton>
      </Box>

      {/* Import Profile Form */}
      <ImportProfileForm
        open={importFormOpen}
        onClose={() => setImportFormOpen(false)}
        onSuccess={fetchProfiles}
        onNotify={({ message, severity }) => setSnackbar({ open: true, message, severity })}
      />

      {/* Table */}
      <TableContainer component={Paper} style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: 'white' }}>
              <TableCell className={classes.headerCell} style={{ width: '50%' }}>
                <Autocomplete
                  className={classes.autocomplete}
                  options={getUniqueValues('label')}
                  value={filters.label === 'all' ? null : filters.label}
                  onChange={(e, newValue) => handleFilterChange('label', newValue)}
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Filter by Label"
                    />
                  )}
                />
              </TableCell>
              <TableCell className={classes.headerCell} style={{ width: '30%' }}>
                <Autocomplete
                  className={classes.autocomplete}
                  options={getUniqueValues('type')}
                  value={filters.type === 'all' ? null : filters.type}
                  onChange={(e, newValue) => handleFilterChange('type', newValue)}
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Filter by Type"
                    />
                  )}
                />
              </TableCell>
              <TableCell className={classes.headerCell} align="center" style={{ width: '20%' }}>
                <strong>View</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((profile, index) => (
              <TableRow
                key={profile.profile_key || index}
                hover
                style={{
                  backgroundColor: '#fff8f0',
                  borderBottom: '8px solid #fff'
                }}
              >
                <TableCell className={classes.tableCell} style={{ fontWeight: 'bold' }}>
                  {profile.label || 'N/A'}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <span className={classes.typeBox}>
                    {profile.profile_type || 'N/A'}
                  </span>
                </TableCell>
                <TableCell className={classes.tableCell} align="center">
                  <IconButton size="small" onClick={() => handleView(profile)} style={{ color: '#ff6f00' }}>
                    <Visibility fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Modal */}
      <Dialog
        open={viewModal}
        onClose={() => setViewModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { height: '80vh' } }}
      >
        <DialogTitle style={{ borderBottom: '1px solid #eee' }}>
          Profile Details - {selectedProfile?.label}
        </DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', padding: '20px', overflow: 'hidden' }}>
          <Box style={{ backgroundColor: '#fff8f0', padding: '16px', borderRadius: '4px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box style={{ backgroundColor: '#fff8f0', padding: '16px', borderRadius: '4px', fontSize: '12px', overflowY: 'auto', flex: 1 }}>
              {selectedProfile && (
                <ReactJson
                  src={selectedProfile}
                  name="profile"
                  collapsed={false}
                  onEdit={false}
                  onAdd={false}
                  onDelete={false}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  theme="bright:inverted"
                  style={{ fontSize: '12px', backgroundColor: '#fff8f0' }}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions style={{ borderTop: '1px solid #eee', padding: '12px 20px' }}>
          <Button onClick={() => setViewModal(false)} style={{ color: '#ff6f00' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profiles;
